import "./AssetInformation.css";

import { AssetInformation as AssetInformationType } from "@repo/api/schemas/policiesInventory.schema";

import { ApplicationIcon, Box, Chip, Typography } from "@saviynt/design-system";
import { memo } from "react";
import { useIntl } from "react-intl";
import { getApplicationSourceIcon } from "../../../../../PoliciesSidepanel/PoliciesSidepanel";
import { isEmpty } from "../../../util";

interface AssetInformationProps {
  assetInformation?: AssetInformationType;
}

const Title = memo(({ text }: { text: string }) => (
  <Typography kind="body3" color="--color-foreground-neutral-subtle">
    {text}
  </Typography>
));

const Description = memo(({ text }: { text: string | null }) => (
  // eslint-disable-next-line formatjs/no-literal-string-in-jsx
  <Typography kind="body2">{text || "-"}</Typography>
));

function AssetInformation({ assetInformation }: AssetInformationProps) {
  const intl = useIntl();

  if (!assetInformation) {
    // eslint-disable-next-line formatjs/no-literal-string-in-jsx
    return <Box>-</Box>;
  }

  const {
    name,
    arn,
    description,
    type,
    nativeType,
    source,
    application,
    tags,
    displayName,
  } = assetInformation;

  return (
    <Box className="asset-information">
      <Typography kind="h3">
        {intl.formatMessage({
          description:
            "Title for the asset information section in NHI asset sidecar showing detailed asset properties",
          defaultMessage: "Asset Information",
          id: "JuAMy6",
        })}
      </Typography>
      <div className="asset-information-content">
        <div className="paragraph-container">
          <Title
            text={intl.formatMessage({
              description:
                "Label for the asset name field in NHI asset information section",
              defaultMessage: "Asset Information",
              id: "g1/0wi",
            })}
          />
          <Description text={name} />
          {/* Show ARN in parentheses only when it differs from the asset name */}
          {name !== arn && (
            <Typography
              kind="body3"
              color="--color-foreground-neutral-subtle"
              className="wrap-text"
            >
              {/* eslint-disable formatjs/no-literal-string-in-jsx  */}({arn})
            </Typography>
          )}
        </div>
        <div className="paragraph-container">
          <Title
            text={intl.formatMessage({
              description:
                "Label for the asset display name field in NHI asset information section",
              defaultMessage: "Display Name",
              id: "emFc41",
            })}
          />
          <Description text={displayName} />
        </div>
        <div className="paragraph-container">
          <Title
            text={intl.formatMessage({
              id: "pmhL4O",
              defaultMessage: "Description",
              description:
                "Label for the description field in NHI asset information section",
            })}
          />
          <Description text={description} />
        </div>
        <div className="paragraph-container">
          <Title
            text={intl.formatMessage({
              id: "r7v6X+",
              defaultMessage: "Type",
              description:
                "Label for the type field in NHI asset information section",
            })}
          />
          <Description text={type} />
        </div>
        <div className="paragraph-container">
          <Title
            text={intl.formatMessage({
              description:
                "Label for the native type field in NHI asset information section",
              defaultMessage: "Native Type",
              id: "Y1AUud",
            })}
          />
          <Description text={nativeType} />
        </div>
        <div className="paragraph-container application">
          <Title
            text={intl.formatMessage({
              id: "MmplML",
              defaultMessage: "Application",
              description:
                "Label for the application field in NHI asset information section",
            })}
          />
          <span className="application">
            <ApplicationIcon
              logo={getApplicationSourceIcon(source.toLowerCase())}
            />
            <Description text={application} />
          </span>
        </div>
        <div className="paragraph-container">
          <Title
            text={intl.formatMessage({
              id: "H/50CG",
              defaultMessage: "Tags",
              description:
                "Label for the tags field in NHI asset information section",
            })}
          />
          <span className="tags">
            {isEmpty(tags) ? (
              // eslint-disable-next-line formatjs/no-literal-string-in-jsx
              <Typography kind="body2">-</Typography>
            ) : (
              Object.entries(tags).map(([key, value]) => (
                <Chip
                  key={key}
                  kind="subtleFilled"
                  label={`${key} : ${value}`}
                />
              ))
            )}
          </span>
        </div>
      </div>
    </Box>
  );
}

export default memo(AssetInformation);
